(() => {
  const root = document.documentElement;
  const storageKey = 'comely-color-mode';
  const validPreferences = new Set(['auto', 'light', 'dark']);
  const desktopMedia = window.matchMedia('(min-width: 1150px)');
  const darkMedia = window.matchMedia('(prefers-color-scheme: dark)');

  const getStoredPreference = () => {
    try {
      const preference = window.localStorage.getItem(storageKey);
      return validPreferences.has(preference) ? preference : null;
    } catch (error) {
      return null;
    }
  };

  const getTheme = (preference) => {
    if (!desktopMedia.matches) return 'light';
    if (preference === 'auto') return darkMedia.matches ? 'dark' : 'light';
    return preference;
  };

  const closeControl = (control, restoreFocus = false) => {
    if (!control) return;
    const trigger = control.querySelector('[data-comely-theme-trigger]');
    const menu = control.querySelector('[data-comely-theme-menu]');
    if (!trigger || !menu) return;
    menu.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    control.classList.remove('is-open');
    if (restoreFocus) trigger.focus();
  };

  const closeControls = (except = null) => {
    document.querySelectorAll('[data-comely-theme-control]').forEach((control) => {
      if (control !== except) closeControl(control);
    });
  };

  const openControl = (control, focusSelected = false) => {
    if (!control) return;
    const trigger = control.querySelector('[data-comely-theme-trigger]');
    const menu = control.querySelector('[data-comely-theme-menu]');
    if (!trigger || !menu) return;
    closeControls(control);
    menu.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    control.classList.add('is-open');
    if (focusSelected) {
      const selected = menu.querySelector('[data-comely-theme-option][aria-checked="true"]');
      selected?.focus();
    }
  };

  const updateControls = (preference, theme) => {
    document.querySelectorAll('[data-comely-theme-control]').forEach((control) => {
      const trigger = control.querySelector('[data-comely-theme-trigger]');
      const activeOption = control.querySelector(`[data-comely-theme-option="${preference}"]`);
      const activeLabel = activeOption?.textContent.trim();
      if (trigger) {
        const label = activeLabel ? `${trigger.dataset.label}: ${activeLabel}` : trigger.dataset.label;
        trigger.setAttribute('aria-label', label);
        trigger.setAttribute('title', label);
      }
      control.querySelectorAll('[data-comely-theme-option]').forEach((option) => {
        option.setAttribute('aria-checked', String(option.dataset.comelyThemeOption === preference));
      });
      control.dataset.comelyTheme = theme;
    });
  };

  const applyPreference = (preference, persist = false) => {
    const normalizedPreference = validPreferences.has(preference) ? preference : 'auto';
    const theme = getTheme(normalizedPreference);
    root.dataset.comelyMode = normalizedPreference;
    root.dataset.comelyTheme = theme;
    root.style.colorScheme = theme;

    const themeColor = document.querySelector('[data-comely-theme-color]');
    if (themeColor) themeColor.setAttribute('content', theme === 'dark' ? '#10151b' : '#ffffff');

    if (persist) {
      try {
        window.localStorage.setItem(storageKey, normalizedPreference);
      } catch (error) {
      }
    }

    updateControls(normalizedPreference, theme);
    window.dispatchEvent(new CustomEvent('comely:theme-change', {
      detail: { preference: normalizedPreference, theme }
    }));
  };

  const initialPreference = validPreferences.has(root.dataset.comelyMode)
    ? root.dataset.comelyMode
    : getStoredPreference() || 'auto';
  applyPreference(initialPreference);

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const trigger = target.closest('[data-comely-theme-trigger]');
    if (trigger) {
      const control = trigger.closest('[data-comely-theme-control]');
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      if (isOpen) closeControl(control);
      else openControl(control);
      return;
    }

    const option = target.closest('[data-comely-theme-option]');
    if (option) {
      applyPreference(option.dataset.comelyThemeOption, true);
      closeControl(option.closest('[data-comely-theme-control]'), true);
      return;
    }

    if (!target.closest('[data-comely-theme-control]')) closeControls();
  });

  document.addEventListener('keydown', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const control = target.closest('[data-comely-theme-control]');
    if (!control) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeControl(control, true);
      return;
    }

    if (target.matches('[data-comely-theme-trigger]') && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      openControl(control, true);
      return;
    }

    if (!target.matches('[data-comely-theme-option]')) return;
    const options = Array.from(control.querySelectorAll('[data-comely-theme-option]'));
    const currentIndex = options.indexOf(target);
    let nextIndex = currentIndex;
    if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % options.length;
    else if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + options.length) % options.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = options.length - 1;
    else return;
    event.preventDefault();
    options[nextIndex].focus();
  });

  document.addEventListener('focusin', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    document.querySelectorAll('[data-comely-theme-control].is-open').forEach((control) => {
      if (!control.contains(target)) closeControl(control);
    });
  });

  const handleSystemChange = () => {
    if (root.dataset.comelyMode === 'auto') applyPreference('auto');
  };
  if (typeof darkMedia.addEventListener === 'function') darkMedia.addEventListener('change', handleSystemChange);
  else darkMedia.addListener(handleSystemChange);

  const handleViewportChange = () => {
    closeControls();
    applyPreference(root.dataset.comelyMode || getStoredPreference() || 'auto');
  };
  if (typeof desktopMedia.addEventListener === 'function') desktopMedia.addEventListener('change', handleViewportChange);
  else desktopMedia.addListener(handleViewportChange);

  document.addEventListener('shopify:section:load', () => {
    updateControls(root.dataset.comelyMode || 'auto', root.dataset.comelyTheme || getTheme('auto'));
  });

  window.requestAnimationFrame(() => {
    root.classList.add('comely-theme-ready');
  });
})();
